import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Grid,
  Chip,
  TextareaAutosize,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import TableChartIcon from '@mui/icons-material/TableChart';
import PrintIcon from '@mui/icons-material/Print';
import ClearIcon from '@mui/icons-material/Clear';
import TablePagination from '@mui/material/TablePagination';

interface Loan {
  loanId: string;
  borrower: string;
  bank: string;
  loanType: string;
  loanReference: string;
  status: 'Active' | 'Closed' | 'Cancelled';
  rateOfInterest: number;
}

interface LoanUtilization {
  utilizationId: string;
  loan: Loan;
  transactionDate: Date;
  utilizationAmount: number;
  valueDate: Date;
  interestRateApplied: number;
  notes: string;
  creationDate: Date;
  lastUpdatedDate: Date;
  createdBy: string;
  lastUpdatedBy: string;
}

const LoanUtilization: React.FC = () => {
  const [loanUtilizations, setLoanUtilizations] = useState<LoanUtilization[]>([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedUtilization, setSelectedUtilization] = useState<LoanUtilization | null>(null);
  const [dialogType, setDialogType] = useState<'add' | 'edit' | 'view' | 'delete'>('add');
  const [searchLoan, setSearchLoan] = useState('');
  const [searchTransactionDate, setSearchTransactionDate] = useState<Date | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const [formData, setFormData] = useState({
    loan: '',
    transactionDate: null as Date | null,
    utilizationAmount: '',
    valueDate: null as Date | null,
    interestRateApplied: '',
    notes: '',
  });

  const [activeLoans, setActiveLoans] = useState<Loan[]>([]);

  useEffect(() => {
    // TODO: Fetch active loans from API
    const mockActiveLoans: Loan[] = [
      {
        loanId: 'LN001',
        borrower: 'Borrower A',
        bank: 'Bank A',
        loanType: 'STL',
        loanReference: 'REF001',
        status: 'Active',
        rateOfInterest: 5.5,
      },
      {
        loanId: 'LN002',
        borrower: 'Borrower B',
        bank: 'Bank B',
        loanType: 'LTL',
        loanReference: 'REF002',
        status: 'Active',
        rateOfInterest: 6.0,
      },
      {
        loanId: 'LN003',
        borrower: 'Borrower C',
        bank: 'Bank C',
        loanType: 'MTL',
        loanReference: 'REF003',
        status: 'Active',
        rateOfInterest: 5.8,
      },
    ];
    setActiveLoans(mockActiveLoans);

    // TODO: Fetch loan utilizations from API
    const mockUtilizations: LoanUtilization[] = [
      {
        utilizationId: 'UT001',
        loan: mockActiveLoans[0],
        transactionDate: new Date('2024-01-01'),
        utilizationAmount: 100000.00,
        valueDate: new Date('2024-01-02'),
        interestRateApplied: 5.5,
        notes: 'Initial utilization',
        creationDate: new Date('2024-01-01'),
        lastUpdatedDate: new Date('2024-01-01'),
        createdBy: 'Admin',
        lastUpdatedBy: 'Admin',
      },
      {
        utilizationId: 'UT002',
        loan: mockActiveLoans[1],
        transactionDate: new Date('2024-02-01'),
        utilizationAmount: 200000.00,
        valueDate: new Date('2024-02-02'),
        interestRateApplied: 6.0,
        notes: 'Second utilization',
        creationDate: new Date('2024-02-01'),
        lastUpdatedDate: new Date('2024-02-01'),
        createdBy: 'Admin',
        lastUpdatedBy: 'Admin',
      },
      {
        utilizationId: 'UT003',
        loan: mockActiveLoans[2],
        transactionDate: new Date('2024-03-01'),
        utilizationAmount: 150000.00,
        valueDate: new Date('2024-03-02'),
        interestRateApplied: 5.8,
        notes: 'Third utilization',
        creationDate: new Date('2024-03-01'),
        lastUpdatedDate: new Date('2024-03-01'),
        createdBy: 'Admin',
        lastUpdatedBy: 'Admin',
      },
      {
        utilizationId: 'UT004',
        loan: mockActiveLoans[0],
        transactionDate: new Date('2024-04-01'),
        utilizationAmount: 120000.00,
        valueDate: new Date('2024-04-02'),
        interestRateApplied: 5.5,
        notes: 'Fourth utilization',
        creationDate: new Date('2024-04-01'),
        lastUpdatedDate: new Date('2024-04-01'),
        createdBy: 'Admin',
        lastUpdatedBy: 'Admin',
      },
      {
        utilizationId: 'UT005',
        loan: mockActiveLoans[1],
        transactionDate: new Date('2024-05-01'),
        utilizationAmount: 180000.00,
        valueDate: new Date('2024-05-02'),
        interestRateApplied: 6.0,
        notes: 'Fifth utilization',
        creationDate: new Date('2024-05-01'),
        lastUpdatedDate: new Date('2024-05-01'),
        createdBy: 'Admin',
        lastUpdatedBy: 'Admin',
      },
      {
        utilizationId: 'UT006',
        loan: mockActiveLoans[2],
        transactionDate: new Date('2024-06-01'),
        utilizationAmount: 170000.00,
        valueDate: new Date('2024-06-02'),
        interestRateApplied: 5.8,
        notes: 'Sixth utilization',
        creationDate: new Date('2024-06-01'),
        lastUpdatedDate: new Date('2024-06-01'),
        createdBy: 'Admin',
        lastUpdatedBy: 'Admin',
      },
    ];
    setLoanUtilizations(mockUtilizations);
  }, []);

  const handleOpenDialog = (type: 'add' | 'edit' | 'view' | 'delete', utilization?: LoanUtilization) => {
    setDialogType(type);
    if (utilization) {
      setSelectedUtilization(utilization);
      setFormData({
        loan: utilization.loan.loanId,
        transactionDate: utilization.transactionDate,
        utilizationAmount: utilization.utilizationAmount.toString(),
        valueDate: utilization.valueDate,
        interestRateApplied: utilization.interestRateApplied.toString(),
        notes: utilization.notes,
      });
    } else {
      setFormData({
        loan: '',
        transactionDate: null,
        utilizationAmount: '',
        valueDate: null,
        interestRateApplied: '',
        notes: '',
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedUtilization(null);
  };

  const handleSave = () => {
    // TODO: Implement save logic
    handleCloseDialog();
  };

  const handleDelete = () => {
    if (selectedUtilization) {
      // TODO: Implement delete logic with validation
      handleCloseDialog();
    }
  };

  const calculateInterestRate = (loanId: string) => {
    const selectedLoan = activeLoans.find(loan => loan.loanId === loanId);
    return selectedLoan ? selectedLoan.rateOfInterest : 0;
  };

  const filteredUtilizations = loanUtilizations.filter(utilization => {
    const matchesLoan = !searchLoan || utilization.loan.loanId === searchLoan;
    const matchesDate = !searchTransactionDate || 
      utilization.transactionDate.toDateString() === searchTransactionDate.toDateString();
    return matchesLoan && matchesDate;
  });

  const paginatedUtilizations = filteredUtilizations.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" sx={{ color: '#374151', fontWeight: 600 }}>
            Loan Utilization
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1, gap: 2 }}>
            <Button
              variant="outlined"
              startIcon={<TableChartIcon />}
              sx={{
                borderRadius: 2,
                fontWeight: 500,
                bgcolor: '#fff',
                color: '#1E293B',
                borderColor: '#E5E7EB',
                textTransform: 'none',
                boxShadow: 'none',
                px: 2.5,
                '&:hover': { bgcolor: '#F3F4F6', borderColor: '#6366F1' }
              }}
            >
              Export to Excel
            </Button>
            <Button
              variant="outlined"
              startIcon={<PrintIcon />}
              sx={{
                borderRadius: 2,
                fontWeight: 500,
                bgcolor: '#fff',
                color: '#1E293B',
                borderColor: '#E5E7EB',
                textTransform: 'none',
                boxShadow: 'none',
                px: 2.5,
                '&:hover': { bgcolor: '#F3F4F6', borderColor: '#6366F1' }
              }}
            >
              Print
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog('add')}
              color="primary"
              sx={{
                fontWeight: 600,
                px: 4,
                borderRadius: 2,
                boxShadow: '0 1px 2px 0 rgba(59,130,246,0.08)',
                textTransform: 'none',
                '&:hover': { bgcolor: '#2563EB' }
              }}
            >
              Add New Utilization
            </Button>
          </Box>
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexWrap: { xs: 'wrap', sm: 'nowrap' },
            alignItems: 'center',
            gap: 2,
            mb: 3,
            p: 2,
            bgcolor: '#F9FAFB',
            border: '1px solid #E5E7EB',
            borderRadius: 3,
            boxShadow: '0 1px 3px 0 rgba(0,0,0,0.03)',
          }}
        >
          <FormControl sx={{ minWidth: 220, maxWidth: 320, bgcolor: '#fff', borderRadius: 2 }} size="small">
            <InputLabel>Loan</InputLabel>
            <Select
              value={searchLoan}
              label="Loan"
              onChange={(e) => setSearchLoan(e.target.value)}
              sx={{ height: 40 }}
            >
              <MenuItem value="">All Loans</MenuItem>
              {activeLoans.map((loan) => (
                <MenuItem key={loan.loanId} value={loan.loanId}>{loan.loanReference} - {loan.borrower}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label="Transaction Date"
              value={searchTransactionDate}
              onChange={(newValue) => setSearchTransactionDate(newValue)}
              slotProps={{ textField: { size: 'small', sx: { minWidth: 180, maxWidth: 220, bgcolor: '#fff', borderRadius: 2, height: 40 } } }}
            />
          </LocalizationProvider>
          <Box sx={{ flexGrow: 1 }} />
          <Button
            variant="contained"
            color="primary"
            startIcon={<SearchIcon />}
            size="medium"
            sx={{ fontWeight: 500, px: 3, borderRadius: 2, boxShadow: 'none', textTransform: 'none', height: 40, minWidth: 100 }}
          >
            Search
          </Button>
          <Button
            variant="contained"
            color="warning"
            startIcon={<ClearIcon />}
            onClick={() => { setSearchLoan(''); setSearchTransactionDate(null); }}
            size="medium"
            sx={{ fontWeight: 500, px: 3, borderRadius: 2, boxShadow: 'none', textTransform: 'none', height: 40, minWidth: 100 }}
          >
            Reset
          </Button>
        </Box>

        <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #E5E7EB', borderRadius: 3, boxShadow: '0 2px 8px 0 rgba(99,102,241,0.06)', background: '#fff', mt: 2 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, color: '#1E293B', bgcolor: '#F9FAFB', fontSize: '1.05rem' }}>Utilization ID</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#1E293B', bgcolor: '#F9FAFB', fontSize: '1.05rem' }}>Loan Reference</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#1E293B', bgcolor: '#F9FAFB', fontSize: '1.05rem' }}>Transaction Date</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#1E293B', bgcolor: '#F9FAFB', fontSize: '1.05rem' }}>Utilization Amount</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#1E293B', bgcolor: '#F9FAFB', fontSize: '1.05rem' }}>Value Date</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#1E293B', bgcolor: '#F9FAFB', fontSize: '1.05rem' }}>Interest Rate Applied</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#1E293B', bgcolor: '#F9FAFB', fontSize: '1.05rem' }}>Notes</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#1E293B', bgcolor: '#F9FAFB', fontSize: '1.05rem' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedUtilizations.map((utilization, idx) => (
                <TableRow
                  key={utilization.utilizationId}
                  hover
                  sx={{
                    '&:hover': { bgcolor: '#F5F6FF' },
                    borderBottom: idx !== paginatedUtilizations.length - 1 ? '1px solid #E5E7EB' : 'none',
                    transition: 'background 0.2s',
                    height: 36
                  }}
                >
                  <TableCell sx={{ py: 0.5 }}>{utilization.utilizationId}</TableCell>
                  <TableCell sx={{ py: 0.5 }}>{utilization.loan.loanReference}</TableCell>
                  <TableCell sx={{ py: 0.5 }}>{utilization.transactionDate.toLocaleDateString()}</TableCell>
                  <TableCell sx={{ py: 0.5 }}>{utilization.utilizationAmount.toLocaleString()}</TableCell>
                  <TableCell sx={{ py: 0.5 }}>{utilization.valueDate.toLocaleDateString()}</TableCell>
                  <TableCell sx={{ py: 0.5 }}>{utilization.interestRateApplied}</TableCell>
                  <TableCell sx={{ py: 0.5 }}>{utilization.notes}</TableCell>
                  <TableCell sx={{ py: 0.5 }}>
                    <IconButton 
                      onClick={() => handleOpenDialog('view', utilization)}
                      sx={{ color: '#6366F1' }}
                    >
                      <VisibilityIcon />
                    </IconButton>
                    <IconButton 
                      onClick={() => handleOpenDialog('edit', utilization)}
                      sx={{ color: '#6366F1' }}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton 
                      onClick={() => handleOpenDialog('delete', utilization)}
                      sx={{ color: '#EF4444' }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={filteredUtilizations.length}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
            rowsPerPageOptions={[5, 10, 25]}
          />
        </TableContainer>

        <Dialog 
          open={openDialog} 
          onClose={handleCloseDialog} 
          maxWidth="md" 
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 2,
            }
          }}
        >
          <DialogTitle sx={{ 
            color: '#374151',
            fontWeight: 600,
            borderBottom: '1px solid #E5E7EB',
            pb: 2
          }}>
            {dialogType === 'add' && 'Add New Utilization'}
            {dialogType === 'edit' && 'Edit Utilization'}
            {dialogType === 'view' && 'View Utilization Details'}
            {dialogType === 'delete' && 'Delete Utilization'}
          </DialogTitle>
          <DialogContent sx={{ pt: 3 }}>
            {dialogType !== 'delete' ? (
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    label="Utilization ID"
                    value={selectedUtilization?.utilizationId || 'Auto-generated'}
                    fullWidth
                    disabled
                    sx={{
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#E5E7EB',
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>Loan</InputLabel>
                    <Select
                      value={formData.loan}
                      onChange={(e) => {
                        const loanId = e.target.value;
                        setFormData({ 
                          ...formData, 
                          loan: loanId,
                          interestRateApplied: calculateInterestRate(loanId).toString()
                        });
                      }}
                      label="Loan"
                      disabled={dialogType === 'view'}
                      sx={{
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#E5E7EB',
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#6366F1',
                        },
                      }}
                    >
                      {activeLoans.map((loan) => (
                        <MenuItem key={loan.loanId} value={loan.loanId}>
                          {loan.loanReference} - {loan.borrower}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <DatePicker
                    label="Transaction Date"
                    value={formData.transactionDate}
                    onChange={(date) => setFormData({ ...formData, transactionDate: date })}
                    disabled={dialogType === 'view'}
                    sx={{
                      width: '100%',
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#E5E7EB',
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#6366F1',
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Utilization Amount"
                    type="number"
                    value={formData.utilizationAmount}
                    onChange={(e) => setFormData({ ...formData, utilizationAmount: e.target.value })}
                    fullWidth
                    disabled={dialogType === 'view'}
                    inputProps={{ step: "0.01" }}
                    sx={{
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#E5E7EB',
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#6366F1',
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <DatePicker
                    label="Value Date"
                    value={formData.valueDate}
                    onChange={(date) => setFormData({ ...formData, valueDate: date })}
                    disabled={dialogType === 'view'}
                    sx={{
                      width: '100%',
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#E5E7EB',
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#6366F1',
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Interest Rate Applied"
                    type="number"
                    value={formData.interestRateApplied}
                    onChange={(e) => setFormData({ ...formData, interestRateApplied: e.target.value })}
                    fullWidth
                    disabled={dialogType === 'view'}
                    inputProps={{ step: "0.0001" }}
                    sx={{
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#E5E7EB',
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#6366F1',
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Notes"
                    multiline
                    rows={4}
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    fullWidth
                    disabled={dialogType === 'view'}
                    sx={{
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#E5E7EB',
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#6366F1',
                      },
                    }}
                  />
                </Grid>
                {selectedUtilization && (
                  <>
                    <Grid item xs={12}>
                      <Typography variant="subtitle1" sx={{ color: '#374151', fontWeight: 600, mb: 1 }}>
                        Audit Information
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Creation Date"
                        value={selectedUtilization.creationDate.toLocaleDateString()}
                        fullWidth
                        disabled
                        sx={{
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#E5E7EB',
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Last Updated Date"
                        value={selectedUtilization.lastUpdatedDate.toLocaleDateString()}
                        fullWidth
                        disabled
                        sx={{
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#E5E7EB',
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Created By"
                        value={selectedUtilization.createdBy}
                        fullWidth
                        disabled
                        sx={{
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#E5E7EB',
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Last Updated By"
                        value={selectedUtilization.lastUpdatedBy}
                        fullWidth
                        disabled
                        sx={{
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#E5E7EB',
                          },
                        }}
                      />
                    </Grid>
                  </>
                )}
              </Grid>
            ) : (
              <Typography sx={{ color: '#374151' }}>
                Are you sure you want to delete this utilization? This action cannot be undone.
              </Typography>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 3, borderTop: '1px solid #E5E7EB' }}>
            <Button 
              onClick={handleCloseDialog}
              sx={{
                color: '#374151',
                textTransform: 'none',
                fontWeight: 500,
                '&:hover': {
                  bgcolor: '#F5F6FF',
                },
              }}
            >
              Cancel
            </Button>
            {dialogType === 'delete' ? (
              <Button 
                onClick={handleDelete} 
                color="error"
                sx={{
                  textTransform: 'none',
                  fontWeight: 500,
                }}
              >
                Delete
              </Button>
            ) : (
              <Button 
                onClick={handleSave} 
                variant="contained"
                sx={{
                  bgcolor: '#6366F1',
                  '&:hover': {
                    bgcolor: '#4F46E5',
                  },
                  textTransform: 'none',
                  fontWeight: 500,
                }}
              >
                Save
              </Button>
            )}
          </DialogActions>
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
};

export default LoanUtilization; 